import { Grid } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import arSaLocale from "date-fns/locale/ar-SA";
import caLocale from "date-fns/locale/ca";
import enGBLocale from "date-fns/locale/en-GB";
import esLocale from "date-fns/locale/es";
import frLocale from "date-fns/locale/fr";
import itLocale from "date-fns/locale/it";
import React, { lazy, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { toast } from "react-toastify";

import { login } from "./_globalStore/redux/reducers/App/AppActionCreators";
import { appSlice } from "./_globalStore/redux/reducers/App/AppSlice";
import { useAppSelector, useAppDispatch } from "./custom-hooks/redux/redux";
import useKeyboardEvent from "./custom-hooks/useKeyboardEvent/useKeyboardEvent";
import { publicRoutes } from "./routes";

import "react-toastify/dist/ReactToastify.css";
import { functionToAddThousandSeparators } from "./utils/number-thousand-separator-utils";

toast.configure();

const Layout = lazy(() => import("./containers/_Layout/Layout"));

const App = () => {
  const { i18n } = useTranslation();
  const applyUserPreferences = useMemo(() => {
    // Extract user preferences from store and pass to function

    functionToAddThousandSeparators(null);
  }, []);

  type LanguagesType = "en" | "fr" | "es" | "it" | "ar" | "ca";

  const localeMap = {
    en: enGBLocale,
    fr: frLocale,
    es: esLocale,
    it: itLocale,
    ar: arSaLocale,
    ca: caLocale,
  };

  const dispatch = useAppDispatch();
  const { hintBackdropShow } = useAppSelector((state) => state.appReducer);
  const { setHintBackdropShow, setIsWidgetsContainerConfigurable } = appSlice.actions;

  useEffect(() => {
    document.dir = i18n.dir();
  }, [i18n, i18n.language]);

  useEffect(() => {
    login()(dispatch);
  }, [dispatch]);

  useEffect(() => {
    // Check for login/logout events in other browser's tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "token" && event.newValue !== event.oldValue) {
        if (!document.hasFocus()) {
          window.location.reload();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const closeHintBackdrop = () => {
    dispatch(setHintBackdropShow(false));
    dispatch(setIsWidgetsContainerConfigurable(true));
  };

  useKeyboardEvent("Escape", (): void => {
    if (hintBackdropShow) closeHintBackdrop();
  });

  const exitFromHelpMode = (target: HTMLDivElement): void => {
    const { parentElement, style, classList } = target as HTMLDivElement;

    if (hintBackdropShow) {
      if (
        parentElement?.classList.contains("hint-button") ||
        // if popover (popup hint text is shown) target will be an invisible div with zIndex -1,
        // so we don't need to closeHintBackdrop;
        style.zIndex === "-1" ||
        classList.contains("widget-popover")
      )
        return;

      closeHintBackdrop();
    }
  };

  return (
    <BrowserRouter>
      <Grid onClick={(e) => exitFromHelpMode(e.target as HTMLDivElement)}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={localeMap[i18n.language as LanguagesType]}
        >
          <Routes>
            {publicRoutes.map(({ path, element }, idx: number) => (
              <Route
                key={idx}
                path={path}
                element={element}
              />
            ))}
            <Route
              path="*"
              element={<Layout />}
            />
          </Routes>
        </LocalizationProvider>
        {/* <CookieWarning /> */}
      </Grid>
    </BrowserRouter>
  );
};

export default App;
